
import { useState, useEffect, useMemo } from 'react';
import { PostCard } from './PostCard';
import { CastPostCard } from './CastPostCard';
import { IsoCastAdPlacement } from './IsoCastAdPlacement';
import { AdPlacement } from './AdPlacement';
import { CollapsibleFeedFilters } from './feed/CollapsibleFeedFilters';
import { FeedContent } from './feed/FeedContent';
import { FeedSkeleton } from './skeleton/FeedSkeleton';
import { useOptimizedPosts } from '@/hooks/posts/useOptimizedPosts';
import { useOptimizedCasts } from '@/hooks/posts/useOptimizedCasts';
import { useCommunities } from '@/hooks/useCommunities';
import { useAnnouncements } from '@/hooks/useAnnouncements';
import { useLiteMode } from '@/hooks/useLiteMode';
import { useOfflineCache } from '@/hooks/useOfflineCache';
import { AuthModal } from './AuthModal';
import { LoadMoreButton } from './feed/LoadMoreButton';
import { Button } from '@/components/ui/button';
import { Gift, LogIn, User, Filter, Wifi, WifiOff } from 'lucide-react';
import { LiteModeBadge } from './LiteModeBadge';
import { ConnectionSettingsButton } from './ConnectionSettingsButton';
import { NetworkStatus } from './NetworkStatus';
import { supabase } from '@/integrations/supabase/client';
import { MyTeamsFarmsList } from './feed/MyTeamsFarmsList';
import { useMobilePerformanceOptimization } from '@/hooks/useMobilePerformanceOptimization';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { useProfile } from '@/hooks/useProfile';

interface FeedProps {
  onTabChange?: (tab: string) => void;
}

export const Feed = ({ onTabChange }: FeedProps = {}) => {
  // Mobile performance optimizations
  const { isMobile, postsLimit, castsLimit } = useMobilePerformanceOptimization();
  const connectionMode = useConnectionMode();
  const isLiteMode = connectionMode === 'lite';
  const { profile } = useProfile();
  
  // Lite mode content type selection (2G/3G optimization)
  const [liteModeContentType, setLiteModeContentType] = useState<'posts' | 'casts' | 'my_teams'>(() => {
    const saved = localStorage.getItem('YS_liteModeContentType');
    return (saved as 'posts' | 'casts' | 'my_teams') || 'posts';
  });
  // Zone filtering state
  const [zoneFilters, setZoneFilters] = useState<{
    activeTab: 'climate' | 'hardiness';
    climateZone?: string;
    country?: string;
    hardinessZone?: string;
    showMyZoneOnly: boolean;
  }>({
    activeTab: 'climate',
    showMyZoneOnly: false,
  });

  // Grants removed from home feed
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [user, setUser] = useState(null);
  
  // Get lite mode and offline cache state
  const { isLiteMode: legacyLiteMode } = useLiteMode();
  const { isOnline, cachedContent, cacheContent, getCacheStatus } = useOfflineCache();

  // Optimized user state management with cleanup
  useEffect(() => {
    let mounted = true;
    
    const checkUser = async () => {
      if (!mounted) return;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (mounted) setUser(user);
      } catch (error) {
        // Silently handle auth errors
        if (mounted) setUser(null);
      }
    };
    
    checkUser();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted) {
        setUser(session?.user ?? null);
      }
    });
    
    return () => {
      mounted = false;
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, []); // Remove user dependency to prevent loops

  // Mode-aware fetch strategy:
  // Full Mode: fetch all types (Posts + Casts + My Teams), tabs only filter view
  // Lite Mode: fetch only active tab type, cancel others on switch
  const displayFilter = liteModeContentType; // What tab user is viewing
  const fetchSet = isLiteMode 
    ? [displayFilter]  // Lite: fetch only active tab
    : ['posts', 'casts', 'my_teams']; // Full: always fetch all
  
  const { 
    posts, 
    loading: postsLoading, 
    error: postsError,
    hasMore: postsHasMore, 
    loadMore: loadMorePosts,
    toggleLike: togglePostLike,
    retryCount: postsRetryCount,
    retry: postsRetry
  } = useOptimizedPosts(zoneFilters, isLiteMode, fetchSet.includes('posts'));
  
  const { 
    casts, 
    loading: castsLoading,
    error: castsError,
    hasMore: castsHasMore, 
    loadMore: loadMoreCasts,
    toggleLike: toggleCastLike,
    retryCount: castsRetryCount,
    retry: castsRetry
  } = useOptimizedCasts(isLiteMode, fetchSet.includes('casts'));
  
  // Cache content when online for offline viewing (optimized)
  useEffect(() => {
    if (isOnline && !postsLoading && !castsLoading && posts.length > 0 && !isMobile) {
      // Only cache on desktop to save mobile memory
      const uniqueProfiles = posts.map(p => p.user).filter((user, index, arr) => 
        arr.findIndex(u => u.name === user.name) === index
      );
      cacheContent(posts.slice(0, 10), casts.slice(0, 5), uniqueProfiles); // Limit cached items
    }
  }, [posts.length, casts.length, postsLoading, castsLoading, isOnline, cacheContent, isMobile]);
  const { communities, loading: communitiesLoading, error: communitiesError, retry: communitiesRetry, retryCount: communitiesRetryCount } = useCommunities({ limit: 10 });
  const { announcements, loading: announcementsLoading, error: announcementsError, retry: announcementsRetry, retryCount: announcementsRetryCount } = useAnnouncements();
  
  // Persist filter states across auth changes using localStorage
  const [postTypeFilter, setPostTypeFilter] = useState(() => {
    const saved = localStorage.getItem('YS_postTypeFilter');
    return saved || 'all';
  });
  
  const [selectedCommunity, setSelectedCommunity] = useState(() => {
    const saved = localStorage.getItem('YS_selectedCommunity');
    return saved || 'all';
  });
  
  const [locationFilter, setLocationFilter] = useState(() => {
    const saved = localStorage.getItem('YS_locationFilter');
    return saved || 'all';
  });
  
  const [hardinesZoneFilter, setHardinesZoneFilter] = useState('all');

  // Persist filter changes to localStorage
  useEffect(() => {
    localStorage.setItem('YS_postTypeFilter', postTypeFilter);
  }, [postTypeFilter]);

  useEffect(() => {
    localStorage.setItem('YS_selectedCommunity', selectedCommunity);
  }, [selectedCommunity]);

  useEffect(() => {
    localStorage.setItem('YS_locationFilter', locationFilter);
  }, [locationFilter]);

  // Persist LiteMode content type selection
  useEffect(() => {
    localStorage.setItem('YS_liteModeContentType', liteModeContentType);
  }, [liteModeContentType]);

  // Grants handling removed from feed

  const loading = postsLoading && castsLoading;

  // Memoize filtered posts to prevent unnecessary recalculations
  const filteredPosts = useMemo(() => {
    return posts.filter(post => post.post_type !== 'cast');
  }, [posts]);

  // Function to parse timestamp and convert to Date for proper sorting
  const parseTimestamp = (timestamp: string): Date => {
    // Handle different timestamp formats
    if (timestamp === 'Just now') return new Date();
    if (timestamp.includes('hour')) {
      const hours = parseInt(timestamp.split(' ')[0]);
      return new Date(Date.now() - hours * 60 * 60 * 1000);
    }
    if (timestamp.includes('day')) {
      const days = parseInt(timestamp.split(' ')[0]);
      return new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    }
    // Try to parse as ISO date first, fallback to current time
    try {
      return new Date(timestamp);
    } catch {
      return new Date();
    }
  };

  // Content type filtering:
  // Lite mode: only show the fetched type (fetchSet has 1 item)
  // Full mode: filter the merged feed by displayFilter (or 'all' for mixed)
  const effectivePostTypeFilter = useMemo(() => {
    if (isLiteMode) {
      // Lite mode: show only the active tab (we only fetched this type)
      return liteModeContentType === 'posts' ? 'videos' 
           : liteModeContentType === 'casts' ? 'casts' 
           : 'my_teams';
    } else {
      // Full mode: allow filtering the merged feed (all types are fetched)
      return postTypeFilter; // User's selected filter or 'all'
    }
  }, [isLiteMode, liteModeContentType, postTypeFilter]);

  // Optimized filtered feed items with minimal re-computation
  const filteredFeedItems = useMemo(() => {
    let result = [];
    
    if (effectivePostTypeFilter === 'videos') {
      result = filteredPosts.filter(post => post.is_video);
    } else if (effectivePostTypeFilter === 'casts') {
      result = casts.map(cast => ({ 
        ...cast, 
        type: 'cast',
        sortTimestamp: cast.created_at
      }));
    } else {
      // For 'all', combine and sort efficiently (only in non-lite mode)
      const postsWithType = filteredPosts.map(post => ({ 
        ...post, 
        type: 'post',
        sortTimestamp: post.created_at || post.timestamp
      }));
      
      const castsWithType = casts.map(cast => ({ 
        ...cast, 
        type: 'cast',
        sortTimestamp: cast.created_at
      }));
      
      result = [...postsWithType, ...castsWithType].sort((a, b) => {
        // Direct timestamp comparison for better performance
        return new Date(b.sortTimestamp).getTime() - new Date(a.sortTimestamp).getTime();
      });
    }
    
    // Optimize community filtering
    if (selectedCommunity !== 'all' && selectedCommunity !== 'see-all') {
      const communityLower = selectedCommunity.toLowerCase();
      result = result.filter(item => {
        if (item.type === 'post') {
          return item.content?.toLowerCase().includes(communityLower) ||
                 item.location_address?.toLowerCase().includes(communityLower) ||
                 item.crop_type?.toLowerCase().includes(communityLower);
        } else {
          return item.iso_request?.toLowerCase().includes(communityLower) ||
                 item.location_address?.toLowerCase().includes(communityLower);
        }
      });
    }
    
    return result;
  }, [filteredPosts, casts, effectivePostTypeFilter, selectedCommunity]);

  // Use filtered feed items directly without grants
  const interspersedFeedItems = useMemo(() => {
    return [...filteredFeedItems];
  }, [filteredFeedItems]);

  const handleLike = (id: string, type: 'post' | 'cast') => {
    if (type === 'post') {
      togglePostLike(id);
    } else {
      toggleCastLike(id);
    }
  };

  const handleComment = (id: string) => {
    console.log('Comment on:', id);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Clear any stale scroll locks on mount
  useEffect(() => {
    document.documentElement.style.overflow = '';
    document.documentElement.style.overflowY = 'auto';
    document.body.style.overflow = '';
    document.body.style.overflowY = 'auto';
  }, []);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <FeedSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Sticky header with filters */}
      <CollapsibleFeedFilters
        postTypeFilter={postTypeFilter}
        onPostTypeFilterChange={setPostTypeFilter}
        selectedCommunity={selectedCommunity}
        onCommunityChange={setSelectedCommunity}
        locationFilter={locationFilter}
        onLocationFilterChange={setLocationFilter}
        communities={communities}
        communitiesLoading={communitiesLoading}
        filteredCount={filteredFeedItems.length}
        totalCount={filteredPosts.length + casts.length}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        zoneFilters={zoneFilters}
        onZoneFiltersChange={setZoneFilters}
        isLiteMode={isLiteMode}
        selectedContentType={liteModeContentType}
        onContentTypeChange={setLiteModeContentType}
      />
      
      <main
        className="max-w-2xl mx-auto px-3 md:px-4 pb-3 md:pb-4 ys-safe-page"
        style={{ paddingBottom: 'calc(var(--ys-footer-h, 64px) + 24px)' }}
      >
        {/* Offline indicator */}
        {!isOnline && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
            <WifiOff className="h-4 w-4 text-yellow-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">
                You're offline
              </p>
              <p className="text-xs text-yellow-600">
                {cachedContent ? 'Showing cached content' : 'Limited content available'}
              </p>
            </div>
          </div>
        )}
        
        <FeedContent
          loading={loading}
          error={null}
          filteredFeedItems={interspersedFeedItems}
          allFeedItems={[...filteredPosts, ...casts]}
          postTypeFilter={effectivePostTypeFilter}
          posts={filteredPosts}
          casts={casts}
          announcements={announcements}
          onLike={handleLike}
          onComment={handleComment}
          onRefresh={handleRefresh}
          locationGrants={[]}
          onSignInRequired={() => setShowAuthModal(true)}
          // Pagination props
          postsHasMore={postsHasMore}
          castsHasMore={castsHasMore}
          onLoadMorePosts={loadMorePosts}
          onLoadMoreCasts={loadMoreCasts}
          postsLoading={postsLoading}
          castsLoading={castsLoading}
          isLiteMode={isLiteMode}
          userLocation={profile?.location}
          userState={profile?.location?.split(',').pop()?.trim()}
        />
      </main>
      
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </div>
  );
};
